<template>
	<div ref="box" v-if="employees.me" class="box" @contextmenu="showContextMenu" @click="showContextMenu">
		<FileUploader :targetUrl="uploadUrl" property="file" fileType="image/*" :onEvent="uploadEvent" ref="EmployeePictureUploader">
			<EmployeePicture :picture="employees.me?.picture" :size="64" :name="employees.me.name"/>
		</FileUploader>
		<presence-icon :state="employees.me?.presence?.state as EmployeePresenceState" :size="24" class="presence-state" />
		<WaitSpinner color="#135" :run="isUploadingPicture" :size="40" shadowColor="#ffffff" class="picture-loading"></WaitSpinner>

		<div class="labels">
			<!-- Employee name and redirect badge if enabled -->
			<div class="name flex gap-2 items-center" v-if="lineCount < 5">
				<div>{{ employees.me.name }}</div>
				<Badge severity="success" size="small" v-if="employees.me.primaryExtension?.redirect">
					<GlyphIcon :icon="GlyphIconName['forward-email']" size="14" color="var(--p-primary-contrast-color)" />
					<div>{{  ss.cutNationalPrefix(employees.me.primaryExtension.redirect, true) }}</div>
				</Badge>
				<Badge severity="danger" size="small" v-if="employees.myPhone?.dnd" :isClickable="true" @click.stop="showSettings('dnd')" class="pulsate">
					<GlyphIcon :icon="GlyphIconName['square-alert']" size="14" color="var(--p-primary-contrast-color)" />
					<div>{{  t('settings.dnd.header') }}</div>
				</Badge>
			</div>

			<!-- Calendar event-->
			<div class="message" v-if="hasCalendar">
				<GlyphIcon :icon="GlyphIconName['calendar']" size="14" color="var(--label-expires-color)" class="icon shrink-0" />
				<div v-if="employees.me.currentOrNextCalendarEvent" class="label flex gap-1 items-center">
					<div class="shrink-0"><b style="letter-spacing: -0.5px;">{{ employees.me.currentOrNextCalendarEvent.time ?? '' }}</b></div>
					<div>{{ employees.me.currentOrNextCalendarEvent.subject }}</div>
					<div v-if="employees.me.currentOrNextCalendarEvent.restOfDay.length > 0" class="flex justify-center items-start shrink-0 calendar-rest-of-day">
						<i class="pi pi-plus" style="font-size:6px;margin-top:3px" />
						<div>{{ employees.me.currentOrNextCalendarEvent.restOfDay.length }}</div>
					</div>
				</div>
				<div v-if="!employees.me.currentOrNextCalendarEvent" class="label">{{ t('common.noEventsToday') }}</div>
			</div>

			<!-- Number presentation -->
			<div class="message">
				<GlyphIcon :icon="GlyphIconName['call']" size="14" color="var(--label-location-color)" class="icon" />
				<div class="label">{{ numberPresentation || 'N/A' }}</div>
			</div>

			<!-- Message -->
			<div class="message" v-if="employees.me?.presence?.message">
				<GlyphIcon :icon="GlyphIconName['chat-message']" size="16" class="icon" />
				<div class="label">{{ employees.me?.presence?.message }}
					<span v-if="employees.me?.presence?.expire && employees.me?.presence?.location"> | {{ employees.me?.presence?.location }}</span>
				</div>
			</div>

			<!-- location -->
			<div class="location" v-if="employees.me?.presence?.location && (!employees.me?.presence?.expire || !employees.me?.presence?.message)">
				<GlyphIcon :icon="GlyphIconName['map-marker']" size="16" color="var(--label-location-color)" class="icon" />
				<div class="label">{{ employees.me.presence.location }}</div>
			</div>

			<!-- expires -->
			<div class="expires" v-if="employees.me?.presence?.expire">
				<GlyphIcon :icon="GlyphIconName['clock']" size="16" color="var(--label-expires-color)" class="icon" />
				<div class="label">{{ expiresLabel }}</div>
			</div>

		</div>

	</div>
	<ContextMenu ref="contextMenu" :model="contextMenuItems" appendTo="body" popup>
		<template #item="{ item, props }">
                <a v-ripple class="flex items-center p-contextmenu-item-link" style="font-size:14px;line-height:21px" v-bind="props.action">
                    <span class="p-contextmenu-item-icon" :class="item.icon" />
                    <span class="p-contextmenu-item-label">{{ item.label }}</span>
                    <Badge v-if="item.badge" class="ml-auto" :value="item.badge" />
					<Badge v-if="item.badgeLeft" severity="secondary" class="" :value="item.badgeLeft" />
                    <span v-if="item.shortcut" class="ml-auto border border-surface rounded bg-emphasis text-muted-color text-xs p-1">{{ item.shortcut }}</span>
					<svg v-if="item.items" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="p-icon p-contextmenu-submenu-icon" aria-hidden="true" data-pc-section="submenuicon">
						<path d="M5.25 11.1728C5.14929 11.1694 5.05033 11.1455 4.9592 11.1025C4.86806 11.0595 4.78666 10.9984 4.72 10.9228C4.57955 10.7822 4.50066 10.5916 4.50066 10.3928C4.50066
							10.1941 4.57955 10.0035 4.72 9.86283L7.72 6.86283L4.72 3.86283C4.66067 3.71882 4.64765 3.55991 4.68275 3.40816C4.71785 3.25642 4.79932 3.11936 4.91585 3.01602C5.03238
							2.91268 5.17819 2.84819 5.33305 2.83149C5.4879 2.81479 5.64411 2.84671 5.78 2.92283L9.28 6.42283C9.42045 6.56346 9.49934 6.75408 9.49934 6.95283C9.49934 7.15158 9.42045
							7.34221 9.28 7.48283L5.78 10.9228C5.71333 10.9984 5.63193 11.0595 5.5408 11.1025C5.44966 11.1455 5.35071 11.1694 5.25 11.1728Z" fill="currentColor"></path>
					</svg>
                </a>
            </template>
	</ContextMenu>
	<SetPresenceModal ref="mySetPresenceModal" />
</template>

<script setup lang="ts">
import EmployeePicture from '@/components/EmployeePicture.vue'
import PresenceIcon from '@/components/PresenceIcon.vue'
import GlyphIcon from '@/components/GlyphIcon.vue'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import WaitSpinner from '@/components/WaitSpinner.vue'
import globalEvents from '@/utilities/GlobalEvents'
import FileUploader from '@/components/FileUploader.vue'
import fileDialog from 'file-dialog'
import { useSettingsStore } from '@/stores/Settings'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useEmployeesStore } from '@/stores/Employees'
import { useElectronStore } from '@/stores/Electron'
import { useI18n } from 'vue-i18n'
import SetPresenceModal from '@/components/modals/SetPresenceModal.vue'
import Kuando from '@/utilities/headsets/Kuando'
import logger from '@/utilities/Logger'
import { computed, onMounted, ref, watch } from 'vue'
import { formatDate } from '@/utilities/filters'
import { EmployeeAccessLevel, EmployeePresenceState, type EmployeeUploadPictureResponse, type EmployeePresence } from '@/TcxApi/Employee'
import { useApiStore } from '@/stores/Api'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import type { PbxAppDashboardListResponseItem } from '@/TcxApi/PbxApp'
import type TieredMenu from 'primevue/tieredmenu'
import type { MenuItem } from 'primevue/menuitem'
import Employee from '@/stores/Employee'
import { PrimeIcons } from '@primevue/core/api'
import { PbxExtensionPhoneType } from '@/TcxApi/PbxExtension'
import { useCallerIdNumbersStore } from '@/stores/CallerIdNumbers'
import { guestMenuItems } from '@/utilities/guestEntryPoints'
import { useGuestInvitesStore } from '@/stores/GuestInvites'

const ss = useSettingsStore()
const dashboards = useDashboardsStore()
const employees = useEmployeesStore()
const callerIdNumbers = useCallerIdNumbersStore()
const guestInvites = useGuestInvitesStore()
const electron = useElectronStore()
const api = useApiStore()
const { t } = useI18n()
const kuando = Kuando.instance
const contextMenu = ref<InstanceType<typeof TieredMenu>>()
const box = ref<HTMLDivElement>()

const isUploadingPicture = ref(false)
const isFullScreen = ref(false)
const mySetPresenceModal = ref<InstanceType<typeof SetPresenceModal>>()

const lineCount = computed(() => {
	let count = 2 // name, callerid
	const pres = employees.me?.presence

	if (pres) {
		if (pres.message) { count++ }
		if (pres.location && !pres.message) { count++ }
		if (pres.expire) { count++ }
		if (employees.me.currentOrNextCalendarEvent) { count++ }
	}

	return count
})

const hasCalendar = computed(() => ss.auth?.customerIntegrations?.ms365?.enabled)

const expiresLabel = computed(() => {
	if (employees.me?.presence && employees.me.presence.expire) {
		let lbl = t('common.presence.' + employees.me.presence.state)
		const expire = new Date(employees.me.presence.expire)
		const inAWeek = new Date()
		const tomorrow = new Date()
		const today = new Date()

		inAWeek.setDate(inAWeek.getDate() + 7)
		inAWeek.setHours(0, 0, 0, 0)
		tomorrow.setDate(tomorrow.getDate() + 1)

		if (expire.getFullYear() === today.getFullYear() && expire.getMonth() === today.getMonth() && expire.getDate() === today.getDate()) {
			// kl. 18:00
			lbl += ` ${t('employee.until')} ${formatDate(expire, 'hh:mm')}`
		} else if (expire.getFullYear() === tomorrow.getFullYear() && expire.getMonth() === tomorrow.getMonth() && expire.getDate() === tomorrow.getDate()) {
			// imorgen kl. 18:00
			if (formatDate(expire, 'hh:mm') === '00:00') {
				lbl += ' ' + t('employee.restOfDay')
			} else {
				lbl += ' ' + t('employee.untilTomorrow') + ' ' + formatDate(expire, 'hh:mm')
			}
		} else if (expire < inAWeek) {
			lbl += ' ' + t('employee.until2') + ' ' + formatDate(expire, 'w')
			if (formatDate(expire, 'hh:mm') != '00:00') {
				lbl += ' ' + t('common.at') + ' ' + formatDate(expire, 'hh:mm')
			}
		} else {
			lbl += ' ' + t('employee.until2') + ' ' + formatDate(expire, 'w')
			lbl += ' ' + t('employee.onThe') + ' ' + formatDate(expire, 'd/M')
			if (formatDate(expire, 'hh:mm') != '00:00') {
				lbl += ' ' + t('common.at') + ' ' + formatDate(expire, 'hh:mm')
			}
		}

		return lbl
	} else {
		return ''
	}
})

const uploadUrl = computed(() => (employees.me ? `/employee/${employees.me._id}/picture` : ''))

const numberPresentation = ref('')

watch(() => employees.me?.extensionSettings, () => {
	logger.info('MyPresence: Employee settings changed, recomputing number presentation')
	numberPresentation.value = computeNumberPresentation()
}, { deep: true, immediate: true })

watch(() => employees.me?.presence?.state, () => {
	logger.info('MyPresence: Employee presence state changed, recomputing number presentation')
	numberPresentation.value = computeNumberPresentation()
})

function computeNumberPresentation() {
	if (!employees.me?.extensionSettings) { return '' }
	const onNextCall = employees.me.extensionSettings.outbound.callerId.onNextCall

	if (onNextCall) {
		// User has set a specific number to be used on next call
		return ss.cutNationalPrefix(onNextCall, true) as string
	}

	if (employees.me.extensionSettings.mobileUseOwnNumber && employees.me.extensionSettings.phones?.find(o => o._id === ss.realPhoneId && o.type === PbxExtensionPhoneType.MVNO)) {
		// User has paired Communicator with a mobile phone that will always use its own number
		return ss.cutNationalPrefix(employees.me.extensionSettings.phones.find(o => o._id === ss.realPhoneId)?.identification as string, true) as string
	}

	if (employees.me?.presence?.state === EmployeePresenceState.OFF_WORK || employees.me?.presence?.state === EmployeePresenceState.HOLIDAY) {
		return ss.cutNationalPrefix(employees.me.extensionSettings.outbound.callerId.onPresenceOffWork as string, true) as string
	} else {
		return ss.cutNationalPrefix(employees.me.extensionSettings.outbound.callerId.onPresenceWork as string, true) as string
	}
}

async function initNumberPresentation() {
	await employees.ready()
	if (employees.me?.extensionSettings) {
		numberPresentation.value = computeNumberPresentation()
	}
}

initNumberPresentation()

const contextMenuItems = computed(() => {
	if (!employees.me) { return [] }

	const alwaysItems: MenuItem[] = [
		{ label: t('mypresence.configuration'), disabled: true, class: 'menu-heading' },
		{ label: isFullScreen.value ? t('mypresence.action.endFullScreen') : t('mypresence.action.startFullScreen'), icon: 'pi pi-window-maximize', command: toggleFullScreen },
		{ label: t('mypresence.action.changeProfilePicture'), icon: 'pi pi-image', command: changePicture },
		{ label: t('mypresence.action.layout'), icon: 'pi pi-objects-column', items: [
			{ label: t('mypresence.action.layout'), disabled: true, class: 'menu-heading' },
			...dashboards.dashboards.map(item => {
				return {
					label: item.name,
					icon: item._id === ss.settings.dashboardId ? PrimeIcons.CHECK : 'no-icon',
					command: () => selectDashboard(item)
				}
			}),
			{ separator: true },
			{ label: t('mypresence.action.layoutNew'), icon: 'pi pi-plus', command: createDashboard, disabled: !ss.isDashboardEditor },
			{ label: t('mypresence.action.layoutEdit'), icon: 'pi pi-pencil', command: editDashboard, disabled: !dashboards.dashboard || !ss.isDashboardEditor },
			{ label: t('mypresence.action.layoutDelete'), icon: 'pi pi-trash', command: deleteDashboard, disabled: !dashboards.dashboard || !ss.isDashboardEditor }
		] },
		{ label: t('mypresence.action.settings'), icon: 'pi pi-cog', command: () => showSettings() },
		{ separator: true },
		{ label: t('mypresence.action.logout'), icon: 'pi pi-sign-out', command: logout },
		{ label: t('mypresence.action.quit'), icon: 'pi pi-power-off', command: quit, visible: ss.runtime.isEmbedded },
		{ label: t('mypresence.endImpersonation'), icon: 'pi pi-sign-out', command: endImpersonation, visible: ss.runtime.gotStash }
	]

	if (!ss.realPhoneId) { return alwaysItems }

	const hideNumberPresentation = !ss.settings.outboundGoto

	const items: MenuItem[] = [
		{ label: 'SMS', icon: 'pi pi-comment', items: [
			{ label: t('mypresence.action.sendSms'), icon: 'pi pi-comment', shortcut: 'CTRL+b', command: showSendSms },
			{ label: t('mypresence.action.sendGroupSms'), icon: 'pi pi-comments', shortcut: 'CTRL+B', command: showSendGroupSms },
		] },
		...guestMenuItems(guestInvites.canCreateGuestInvites, t, globalEvents.emit),
		{ label: t('mypresence.action.calendar'), icon: 'pi pi-calendar', shortcut: 'CTRL+k', command: () => showCalendar(), visible: hasCalendar.value },
		{ separator: true },
		{ label: t('mypresence.status'), disabled: true, class: 'menu-heading' },
		{ label: t('mypresence.action.setPresence'), icon: 'pi pi-user', command: showSetPresenceModal },
		{ label: t('mypresence.action.quickSetPresence'), icon: 'pi pi-user-plus', items: [
			{ label: t('common.presence.AVAILABLE'), icon: 'pi pi-circle-fill presence-available-color', command: () => setPresenceState(EmployeePresenceState.AVAILABLE) },
			{ label: t('common.presence.BUSY'), icon: 'pi pi-circle-fill presence-busy-color', command: () => setPresenceState(EmployeePresenceState.BUSY) },
			{ label: t('common.presence.OUT_OF_OFFICE'), icon: 'pi pi-circle-fill presence-out-of-office-color', command: () => setPresenceState(EmployeePresenceState.OUT_OF_OFFICE) },
			{ label: t('common.presence.OFF_WORK'), icon: 'pi pi-circle-fill presence-off-work-color', command: () => setPresenceState(EmployeePresenceState.OFF_WORK) },
			{ label: t('common.presence.HOLIDAY'), icon: 'pi pi-circle-fill presence-holiday-color', command: () => setPresenceState(EmployeePresenceState.HOLIDAY) },
			{ separator: true },
			{ label: t('mypresence.action.busy15Minutes'), icon: 'pi pi-clock presence-busy-color', command: () => setPresenceState(EmployeePresenceState.BUSY, '15_MINUTES') },
			{ label: t('mypresence.action.busy30Minutes'), icon: 'pi pi-clock presence-busy-color', command: () => setPresenceState(EmployeePresenceState.BUSY, '30_MINUTES') },
			{ label: t('mypresence.action.busy1Hour'), icon: 'pi pi-clock presence-busy-color', command: () => setPresenceState(EmployeePresenceState.BUSY, '1_HOUR') },
			{ label: t('mypresence.action.busy2Hours'), icon: 'pi pi-clock presence-busy-color', command: () => setPresenceState(EmployeePresenceState.BUSY, '2_HOURS') },
			{ label: t('mypresence.action.busyRestOfDay'), icon: 'pi pi-clock presence-busy-color', command: () => setPresenceState(EmployeePresenceState.BUSY, 'REST_OF_DAY') },
			{ label: t('mypresence.action.offWorkRestOfDay'), icon: 'pi pi-clock presence-off-work-color', command: () => setPresenceState(EmployeePresenceState.OFF_WORK, 'REST_OF_DAY') },
			{ label: t('mypresence.action.offWorkRestOfWeek'), icon: 'pi pi-clock presence-off-work-color', command: () => setPresenceState(EmployeePresenceState.OFF_WORK, 'REST_OF_WEEK') },
			{ label: t('mypresence.action.offWorkRestOfMonth'), icon: 'pi pi-clock presence-off-work-color', command: () => setPresenceState(EmployeePresenceState.OFF_WORK, 'REST_OF_MONTH') }
		] },
		{
			visible: hideNumberPresentation,
			label: t('mypresence.action.numberPresentation'),
			icon: 'pi pi-id-card',
			items: [
				{
					label: t('mypresence.action.numberPresentationWorking'),
					items: [
						...callerIdNumbers.numbers.map(number => {
							if (number.number === '---') {
								return { separator: true }
							} else {
								return {
									label: ss.cutNationalPrefix(number.number, true),
									badgeLeft: number.name ?? '',
									icon: number.number === employees.me?.extensionSettings?.outbound.callerId.onPresenceWork ? 'pi pi-check' : 'no-icon',
									command: () => setCallerIdOnPresenceWork(number.number)
								}
							}
						}),
						{ separator: true },
						{
							label: t('mypresence.action.numberPresentationSecret'),
							icon: employees.me?.extensionSettings?.outbound.callerId.onPresenceWork === 'SECRET' ? 'pi pi-check' : 'no-icon',
							command: () => setCallerIdOnPresenceWork('SECRET')
						}
					]
				},
				{
					label: t('mypresence.action.numberPresentationOffWork'),
					items: [
						...callerIdNumbers.numbers.map(number => {
							if (number.number === '---') {
								return { separator: true }
							} else {
								return {
									label: ss.cutNationalPrefix(number.number, true),
									badgeLeft: number.name ?? '',
									icon: number.number === employees.me?.extensionSettings?.outbound.callerId.onPresenceOffWork ? 'pi pi-check' : 'no-icon',
									command: () => setCallerIdOnPresenceOffWork(number.number)
								}
							}
						}),
						{ separator: true },
						{
							label: t('mypresence.action.numberPresentationSecret'),
							icon: employees.me?.extensionSettings?.outbound.callerId.onPresenceOffWork === 'SECRET' ? 'pi pi-check' : 'no-icon',
							command: () => setCallerIdOnPresenceOffWork('SECRET')
						}
					]
				},
				{ separator: true },
				{
					label: t('mypresence.action.numberPresentationNextCall'),
					items: [
						{
							label: t('common.doNotUse'),
							icon: !employees.me?.extensionSettings?.outbound.callerId.onNextCall ? 'pi pi-check' : 'no-icon',
							command: () => setCallerIdOnNextCall(null)
						},
						{ separator: true },
						...callerIdNumbers.numbers.map(number => {
							if (number.number === '---') {
								return { separator: true }
							} else {
								return {
									label: ss.cutNationalPrefix(number.number, true),
									badgeLeft: number.name ?? '',
									icon: number.number === employees.me?.extensionSettings?.outbound.callerId.onNextCall ? 'pi pi-check' : 'no-icon',
									command: () => setCallerIdOnNextCall(number.number)
								}
							}
						}),
						{ separator: true },
						{
							label: t('mypresence.action.numberPresentationSecret'),
							icon: employees.me?.extensionSettings?.outbound.callerId.onNextCall === 'SECRET' ? 'pi pi-check' : 'no-icon',
							command: () => setCallerIdOnNextCall('SECRET')
						}
					]
				}
			]
		},
		{ label: t('mypresence.action.unconditionalCallForward'), icon: 'pi pi-arrow-right', command: showRedirectModal },
		{ separator: true },
		...alwaysItems
	]

	return items
})

async function showContextMenu(event: MouseEvent) {
	event.preventDefault()
	contextMenu.value?.toggle(event)
}

function toggleFullScreen() {
	if (ss.runtime.isEmbedded) {
		electron.setFullscreen(!isFullScreen.value)
		ss.settings.fullscreen = !isFullScreen.value
		logger.info(`MyPresence: ${isFullScreen.value ? 'Disabled' : 'Enabled'} fullscreen mode via Electron`)
	} else {
		if (!isFullScreen.value) { // NOSONAR
			document.documentElement.requestFullscreen()
			ss.settings.fullscreen = true
			logger.info('MyPresence: Enabled fullscreen mode in browser')
		} else {
			document.exitFullscreen()
			ss.settings.fullscreen = false
			logger.info('MyPresence: Disabled fullscreen mode in browser')
		}
	}
}

function showSetPresenceModal() {
	mySetPresenceModal.value?.show()
}

function showCalendar() {
	if (employees.me) {
		globalEvents.emit('CommonCalendarModal', employees.me)
	}
}

async function setPresenceState(state: EmployeePresenceState, expires?: '15_MINUTES' | '30_MINUTES' | '1_HOUR' | '2_HOURS' | 'REST_OF_DAY' | 'REST_OF_WEEK' | 'REST_OF_MONTH') {
	if (!expires) {
		const presence: EmployeePresence = { state: state, expire: null, overrides: [] }

		try {
			await api.post(`/employee/${ss.auth?.employee}`, { presence: presence })
			logger.info(`MyPresence: User changed presence to: ${state}`)
		}
		catch (err) {
			globalEvents.emit('CommonErrorModal', { header: t('mypresence.setPresenceErrorHeader'), message: t('mypresence.setPresenceErrorMessage') + '<br><br>' + (err as Error).message })
			logger.error(`MyPresence: User failed to set state: ${(err as Error).message}`)
		}
		return
	}

	const date = new Date()

	if (expires) {
		switch (expires) {
			case '15_MINUTES':
				date.setMinutes(date.getMinutes() + 15)
				break
			case '30_MINUTES':
				date.setMinutes(date.getMinutes() + 30)
				break
			case '1_HOUR':
				date.setHours(date.getHours() + 1)
				break
			case '2_HOURS':
				date.setHours(date.getHours() + 2)
				break
			case 'REST_OF_DAY':
				date.setHours(0, 0, 0, 0)
				date.setDate(date.getDate() + 1)
				break
			case 'REST_OF_WEEK': {
				date.setHours(0, 0, 0, 0)
				const weekDay = date.getDay()
				if (weekDay == 0) {
					date.setDate(date.getDate() + 1)
				} else if (weekDay == 1) {
					date.setDate(date.getDate() + 7)
				} else if (weekDay > 1) {
					date.setDate(date.getDate() + (8 - weekDay))
				}
				break
			}
			case 'REST_OF_MONTH':
				date.setHours(0, 0, 0, 0)
				date.setDate(1)
				date.setMonth(date.getMonth() + 1)
				break
		}
	}

	const presence: EmployeePresence = { state: state, expire: (expires ? date : null), overrides: [] }

	try {
		await api.post(`/employee/${ss.auth?.employee}`, { presence: presence })
		logger.info(`MyPresence: User changed presence to: ${state}${expires ? ' that expires: ' + date.toLocaleString() : ''}`)
	}
	catch (err) {
		globalEvents.emit('CommonErrorModal', { header: t('mypresence.setPresenceErrorHeader'), message: t('mypresence.setPresenceErrorMessage') + '<br><br>' + (err as Error).message })
		logger.error(`MyPresence: User failed to set state: ${(err as Error).message}`)
	}
}

function uploadEvent(args: { state: string, file: string, response?: EmployeeUploadPictureResponse | ErrorResponseDetail | unknown }) {
	if (args.state === 'FAILED') {
		isUploadingPicture.value = false
		globalEvents.emit('CommonErrorModal', { header: t('mypresence.errorUploadHeader'), message: t('mypresence.errorUploadMessage') })
		logger.error(`MyPresence: Failed to upload picture: ${args.file} - ${JSON.stringify(args.response)}`)
	}
	if (args.state === 'COMPLETED') {
		isUploadingPicture.value = false
		logger.info(`MyPresence: User uploaded new picture, it is now: ${(args.response as EmployeeUploadPictureResponse).picture}`)
	}
	if (args.state === 'UPLOADING') {
		isUploadingPicture.value = true
	}
}

async function changePicture() {
	const files = await fileDialog({ accept: 'image/*', multiple: false })
	if (!files[0]) { return }
	const file = files[0].name
	const data = new FormData()

	uploadEvent({ state: 'UPLOADING', file: file })

	try {
		data.append('file', files[0], files[0].name)
		const res = await api.post<EmployeeUploadPictureResponse>(`/employee/${employees.me?._id}/picture`, data, { headers: { 'content-type': 'multipart/form-data' } })

		uploadEvent({ state: 'COMPLETED', file: file, response: res.data })
	}
	catch (err) {
		uploadEvent({ state: 'FAILED', file: file, response: err as ErrorResponseDetail })
	}
}

function selectDashboard(item: PbxAppDashboardListResponseItem) {
	ss.settings.dashboardId = item._id
	globalEvents.emit('Dashboard:Select', item._id)
	logger.info(`MyPresence: User selected dashboard ${item._id}`)
}

function createDashboard() {
	globalEvents.emit('Dashboard:Create')
	logger.info('MyPresence: User started create dashboard')
}

function editDashboard() {
	globalEvents.emit('Dashboard:Edit')
	logger.info('MyPresence: User started edit dashboard')
}

function deleteDashboard() {
	if (!dashboards.dashboard) { return }
	if (dashboards.dashboard.shared && !ss.accessLevelIsMin(EmployeeAccessLevel.MANAGER)) {
		globalEvents.emit('CommonErrorModal', { header: t('dashboards.sharedDeleteAccessDeniedHeader'), message: t('dashboards.sharedDeleteAccessDeniedMessage') })
		return
	}
	globalEvents.emit('CommonQuestionModal', { header: t('mypresence.layoutDeleteHeader'), message: t('mypresence.layoutDeleteMessage').replace('%%name%%', dashboards.dashboard.name),
		ok: t('common.delete'), emit: 'Dashboard:Delete', emitData: ss.settings.dashboardId })
}

function logout() {
	globalEvents.emit('CommonQuestionModal', { header: t('mypresence.logoutHeader'), message: t('mypresence.logoutMessage'), ok: t('common.yes'), cancel: t('common.no'), emit: 'Auth:InitiateLogout' })
}

function logoutDo() {
	logger.info('User initiated logout')
	setTimeout(() => { ss.logout() }, 500)
}

async function quit() {
	if (ss.runtime.isEmbedded) {
		electron.terminate()
	}
}

function showRedirectModal() {
	if (employees.me) {
		globalEvents.emit('CommonSetRedirectModal', employees.me as unknown as Employee)
	}
}

function showSettings(section?: string) {
	logger.log('Show settings clicked')
	globalEvents.emit('CommonSettings', section)
}

async function setCallerIdOnPresenceWork(number: string) {
	try {
		await api.post(`/pbx/extension/${employees.me?.primaryExtension?._id}`, { outbound: { callerId: { onPresenceWork: number } } })
		logger.info(`MyPresence: User set caller ID on presence work to ${number}`)
	}
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('mypresence.numberPresentationErrorHeader'), message: t('mypresence.numberPresentationErrorMessage1') })
	}
}

async function setCallerIdOnPresenceOffWork(number: string) {
	try {
		await api.post(`/pbx/extension/${employees.me?.primaryExtension?._id}`, { outbound: { callerId: { onPresenceOffWork: number } } })
		logger.info(`MyPresence: User set caller ID on presence off work to ${number}`)
	}
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('mypresence.numberPresentationErrorHeader'), message: t('mypresence.numberPresentationErrorMessage2') })
	}
}

async function setCallerIdOnNextCall(number: string | null) {
	try {
		await api.post(`/pbx/extension/${employees.me?.primaryExtension?._id}`, { outbound: { callerId: { onNextCall: number } } })
		logger.info(`MyPresence: User set caller ID on next call to ${number}`)
	}
	catch (_) {
		globalEvents.emit('CommonErrorModal', { header: t('mypresence.numberPresentationErrorHeader'), message: t('mypresence.numberPresentationErrorMessage3') })
	}
}

function showSendSms() {
	globalEvents.emit('CommonSendSmsModal', { employee: undefined, number: undefined })
}

function showSendGroupSms() {
	globalEvents.emit('CommonSendGroupSmsModal')
}

function endImpersonation() {
	ss.endImpersonation()
}

onMounted(async () => {
	globalEvents.on('Auth:InitiateLogout', logoutDo)
	globalEvents.on('OneSecond', () => {
		// this.isFullScreen = window.outerHeight >= screen.availHeight && window.outerWidth >= screen.availWidth;
		if (ss.runtime.isEmbedded) {
			isFullScreen.value = ss.settings.fullscreen
		} else {
			isFullScreen.value = document.fullscreenElement != null
		}
	})
	globalEvents.on('MyPresenceToggleMenu', () => {
		box.value?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX: 30, clientY: 30 }))
	})

	if (ss.settings.fullscreen) {
		document.documentElement.requestFullscreen()
	}

	// Remove default title on file picker for employee picture
	const fileElements = document.querySelectorAll('input[type=\'file\'][accept=\'image/*\']')
	if (fileElements.length > 0) { (fileElements[0] as HTMLInputElement).title = '' }

	await employees.ready()
	if (employees.me?.extensionSettings) {
		numberPresentation.value = computeNumberPresentation()
	}
})

// This will ensure that if presence changes and the user has no active calls, then the Kuando light will be set to idle which will take the user current presence state into account
watch(() => employees.me?.presence?.state, () => {
	if (employees.me?.calls.length === 0) {
		kuando.idle()
	}
})
</script>

<style scoped>
	.box {
		-webkit-app-region: no-drag;
		display: flex;
		cursor: context-menu;
		border-radius: 30px 5px 5px 30px;
		transition: background 400ms;
		background: var(--my-presence-background);
	}
	.box:hover {
		background: var(--my-presence-background-hover);
	}

	.name {
		font-size: 16px;
		font-weight: 400;
		line-height: 17px;
		/* letter-spacing: 0.05em; */
	}
	.message {
		font-size: 11px;
		color: var(--p-text-color);
		line-height: 12px;
		display: flex;
	}
	.location {
		font-size: 11px;
		color: var(--p-text-color);
		line-height: 12px;
		display: inline-flex;
	}
	.expires {
		font-size: 11px;
		font-style: italic;
		color: #855;
		line-height: 11px;
		display: inline-flex;
	}
	.labels {
		display: flex;
		flex-direction: column;
		justify-content: center;
		margin-left: -10px;
		width: 250px;
		/* Still 64px of lines, but the clip reaches 2px beyond them for the first line's accents and the last line's descenders */
		max-height: 68px;
		padding: 2px 0;
		margin-top: -2px;
		margin-bottom: -2px;
		overflow: hidden;
	}
	/* Clip sideways only, for the ellipsis: the line-heights are tighter than Inter's accents (the ring on Å)
		and descenders, so clipping vertically too shaved the tops and tails off the letters */
	.labels DIV {
		overflow: clip visible;
		min-width: 0; /* overflow:hidden let flex items shrink below their text, clip does not */
		min-height: 0;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.presence-state {
		position: relative;
		left: -14px;
		top: 40px;
		flex-shrink: 0;
		height: 24px;
	}
	.icon {
		margin-top: -1px;
		flex: 0 0 auto;
	}
	.label {
		padding-left: 2px;
	}
	/* Room for the synthesized italic overhang of the last glyph, which the ellipsis clip would cut */
	.expires .label {
		padding-right: 0.25em;
	}
	.picture-loading {
		position: absolute;
		left: 22px;
		top: 15px;
	}
	.menu-active::before {
		content: "✔ ";
		opacity: 0.8;
	}
	.redirect {
		/* font-size: var(--font-size-small); */
		/* font-weight: 400; */
		/* background: var(--bg-color-dashboard-header); */
		padding: 2px 2px;
		color: var(--p-highlight-color);
		background: var(--p-highlight-background);
		height: 15px;
		border-radius: 3px;
	}

	.menu-heading {
		font-weight: bold;
		font-size: 0.9rem;
		color: var(--p-text-muted-color);
	}
	.calendar-rest-of-day {
		color: var(--p-primary-contrast-color);
		background: var(--p-surface-400);
		border-radius: 4px;
		padding: 0px 3px;
		margin-right: 2px;
		font-size: 8px;
	}
	.numberlabel {
		color: #BBAC91;
		font-size: 0.9rem;
	}

	.pulsate {
		animation: pulsate 2s infinite;
	}

	@keyframes pulsate {
		0% {
			opacity: 0.6;
		}

		50% {
			opacity: 1;
		}

		100% {
			opacity: 0.6;
		}
	}
</style>
